import styled from "styled-components";
import { typographyMixin } from "../design-system/tokens/typography";
import { Button } from "../design-system/components/primitives/Button/Button";
import { Input } from "../design-system/components/primitives/Input/Input";
import { authColors } from "./authTokens";

/**
 * Thin styled wrappers around design system primitives that apply
 * Figma auth spec overrides with guaranteed specificity.
 *
 * Figma specs:
 * - Input: Body 4 (14px/400), padding 8px 16px, radius 8px, width 311px (fills parent)
 * - Primary Button: Body 3 (14px/500), padding 8px 16px, radius 8px, height 36px
 * - OAuth Button: Body 3 (14px/500), padding 8px 16px, radius 8px, hug height
 * - Icon: 20×20px
 */

export const AuthButton = styled(Button)`
  && {
    ${typographyMixin("body", 3)}
    padding: 8px 16px;
    min-height: 36px;
    border-radius: 8px;
    box-shadow: ${authColors.inputShadow};
    gap: 16px;
  }
       &:disabled {
      background: ${authColors.buttonPrimaryDisabled};
      border-color: ${authColors.buttonPrimaryDisabled};
      color: ${authColors.buttonPrimaryText};
      cursor: not-allowed;
    }
`;

export const AuthInput = styled(Input)`
  /* Target the InputContainer (the div with the border) */
  & > div:has(> input),
  &:has(> input) {
    padding: 8px 16px;
    border-radius: 8px;
    box-shadow: ${authColors.inputShadow};
    min-height: unset;
  }

  /* Disabled container: subtle gray fill matching Figma */
  & > div:has(> input:disabled),
  &:has(> input:disabled) {
    background-color: #F4F6FB;
  }

  input {
    ${typographyMixin("body", 4)}
  }

  input:disabled {
    color: ${authColors.textMedEmphasis};
    -webkit-text-fill-color: ${authColors.textMedEmphasis};
  }
`;

export const AuthFormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.base[3]};
  width: 100%;
`;

export const AuthFieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.base[3]};
  width: 100%;

  label {
    ${typographyMixin("body", 4)}
    color: ${authColors.textMedEmphasis};
  }
`;

export const AuthOAuthGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.base[3]};
  width: 100%;
`;

export const AuthErrorMsg = styled.p`
  ${typographyMixin("caption", 4)}
  color: ${authColors.error};
  margin: 0;
  text-align: center;
  width: 100%;
`;

export const AuthForgotLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  ${typographyMixin("caption", 3)}
  color: ${authColors.buttonPrimary};
  align-self: flex-end;
  margin-top: 4px;

  &:hover {
    text-decoration: underline;
  }
`;

export const AuthBodyText = styled.p`
  ${typographyMixin("body", 4)}
  color: ${authColors.textHighEmphasis};
  margin: 0;
  text-align: left;
  width: 100%;
`;

/** Shown during async auth actions so users see progress (pairs with button loading). */
export const AuthLoadingStatus = styled.p`
  ${typographyMixin("caption", 4)}
  color: ${authColors.textMedEmphasis};
  margin: 0;
  text-align: center;
  width: 100%;
  min-height: 1.25em;
`;

/** Replaces a disabled primary button during resend cooldown — readable text + clear purpose. */
export const AuthCooldownNotice = styled.div`
  width: 100%;
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid ${authColors.inputBorder};
  background: ${authColors.white};
  box-shadow: ${authColors.inputShadow};
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: center;
`;

export const AuthCooldownTitle = styled.p`
  ${typographyMixin("body", 4)}
  font-weight: 600;
  color: ${authColors.textHighEmphasis};
  margin: 0;
`;

export const AuthCooldownDetail = styled.p`
  ${typographyMixin("caption", 4)}
  color: ${authColors.textMedEmphasis};
  margin: 0;
`;

export const AuthOutlineButton = styled(Button)`
  && {
    ${typographyMixin("body", 3)}
    padding: 8px 16px;
    min-height: 36px;
    border-radius: 8px;
    box-shadow: ${authColors.inputShadow};
    gap: 16px;
    background: transparent;
    color: ${authColors.buttonPrimary};
    border: 1px solid ${authColors.buttonPrimary};

    &:hover {
      background: ${authColors.buttonPrimary};
      color: ${authColors.buttonPrimaryText};
    }
  }
`;
